import React from "react";
import "../css/NewYearTheme.css";

/** New Year — active from Dec 31 through the end of January */
export const isNewYear = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  return month === 1 || (month === 12 && now.getDate() === 31);
};

/** The year being welcomed — next year from July onward, this year in Jan–Jun */
const getNewYear = () => {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
};

/* ------------------------------------------------------------------ */
/* Fireworks — a rocket trail rises, then bursts into sparks           */
/* ------------------------------------------------------------------ */
const BURSTS = [
  { left: "14%", top: "34px", delay: 0, color: "gold" },
  { left: "31%", top: "22px", delay: 1.4, color: "rose" },
  { left: "46%", top: "40px", delay: 2.6, color: "cyan" },
  { left: "58%", top: "18px", delay: 0.8, color: "violet" },
  { left: "6%", top: "20px", delay: 3.3, color: "cyan" },
];

const SPARK_COUNT = 12;

const Firework = ({ left, top, delay, color }) => (
  <span
    className={`newyear-firework newyear-firework--${color}`}
    style={{ left, top, "--ny-delay": `${delay}s` }}
  >
    <span className="newyear-firework__trail" />
    <span className="newyear-firework__burst">
      {Array.from({ length: SPARK_COUNT }).map((_, i) => (
        <span
          key={i}
          className="newyear-spark"
          style={{ "--ny-angle": `${(360 / SPARK_COUNT) * i}deg` }}
        />
      ))}
    </span>
  </span>
);

export const Fireworks = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-fireworks" aria-hidden="true">
      {BURSTS.map((b, i) => (
        <Firework key={i} {...b} />
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Confetti — a light sprinkle, not a blizzard                         */
/* ------------------------------------------------------------------ */
const CONFETTI = [
  { left: "4%", delay: 0, duration: 5.2, color: "#f5c542" },
  { left: "12%", delay: 2.1, duration: 6.4, color: "#e84a7f" },
  { left: "20%", delay: 3.9, duration: 5.6, color: "#d9d9e3" },
  { left: "29%", delay: 1.2, duration: 6.8, color: "#3fc6e0" },
  { left: "37%", delay: 4.6, duration: 5.4, color: "#f5c542" },
  { left: "45%", delay: 0.7, duration: 6.1, color: "#9b6cf0" },
  { left: "53%", delay: 3.1, duration: 5.9, color: "#e84a7f" },
  { left: "62%", delay: 1.8, duration: 6.6, color: "#f5c542" },
  { left: "71%", delay: 5.2, duration: 5.3, color: "#3fc6e0" },
  { left: "80%", delay: 2.7, duration: 6.2, color: "#d9d9e3" },
  { left: "89%", delay: 0.4, duration: 5.7, color: "#9b6cf0" },
  { left: "96%", delay: 4.1, duration: 6.5, color: "#f5c542" },
];

export const Confetti = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-confetti" aria-hidden="true">
      {CONFETTI.map((c, i) => (
        <span
          key={i}
          className="newyear-confetto"
          style={{
            left: c.left,
            background: c.color,
            animationDelay: `${c.delay}s`,
            animationDuration: `${c.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Champagne glass — the pair tilts in to clink every few seconds      */
/* ------------------------------------------------------------------ */
const ChampagneGlass = ({ side }) => (
  <svg
    className={`newyear-glass newyear-glass--${side}`}
    viewBox="0 0 30 60"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M6 4 h18 q1 16 -3 22 q-3 4 -6 4 q-3 0 -6 -4 q-4 -6 -3 -22z"
      fill="rgba(255, 255, 255, 0.25)"
      stroke="#c9a227"
      strokeWidth="1.6"
    />
    <path
      d="M7.2 11 h15.6 q0 11 -2.8 15 q-2.4 3 -5 3 q-2.6 0 -5 -3 q-2.8 -4 -2.8 -15z"
      fill="#f5c542"
      opacity="0.85"
    />
    <circle className="newyear-bubble" cx="13" cy="22" r="1" fill="#fff8d6" />
    <circle
      className="newyear-bubble newyear-bubble--2"
      cx="17"
      cy="25"
      r="0.8"
      fill="#fff8d6"
    />
    <path d="M15 30 v20" stroke="#c9a227" strokeWidth="1.8" />
    <path
      d="M8 54 q7 -5 14 0z"
      fill="#c9a227"
      stroke="#c9a227"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Banner — "Season's Greetings" with a gold shimmer and the year      */
/* ------------------------------------------------------------------ */
export const NewYearBanner = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-banner" aria-hidden="true">
      <span className="newyear-cheers">
        <ChampagneGlass side="left" />
        <span className="newyear-cheers__sparkle">✦</span>
        <ChampagneGlass side="right" />
      </span>
      <span className="newyear-banner__text">
        <span className="newyear-banner__greeting">Season's Greetings</span>
        <span className="newyear-banner__year">{getNewYear()}</span>
      </span>
      <PartyPopper />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Party popper — fires curly streamers out of the cone every few secs */
/* ------------------------------------------------------------------ */
const POPPER_PIECES = [
  { color: "#f5c542", x: "26px", y: "-30px", r: "200deg" },
  { color: "#ff5d8f", x: "36px", y: "-14px", r: "-160deg" },
  { color: "#3fd6f0", x: "14px", y: "-38px", r: "260deg" },
  { color: "#a779ff", x: "40px", y: "-28px", r: "-220deg" },
  { color: "#f5c542", x: "30px", y: "-4px", r: "180deg" },
  { color: "#ff5d8f", x: "20px", y: "-22px", r: "-240deg" },
];

const PartyPopper = () => (
  <span className="newyear-popper">
    <svg
      className="newyear-popper__cone"
      viewBox="0 0 30 30"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 28 L10 8 L22 20z" fill="#a779ff" />
      <path d="M5.2 20 L8.2 12.5 L14 18.5 L8 22.5z" fill="#f5c542" />
      <path d="M3.4 24.4 L4.6 21.4 L7.4 24.2 L5 25.4z" fill="#ff5d8f" />
      <ellipse
        cx="16"
        cy="14"
        rx="2.4"
        ry="8.6"
        transform="rotate(-45 16 14)"
        fill="#7b4fd6"
      />
    </svg>
    {POPPER_PIECES.map((p, i) => (
      <span
        key={i}
        className="newyear-popper__piece"
        style={{
          background: p.color,
          "--ny-x": p.x,
          "--ny-y": p.y,
          "--ny-r": p.r,
          animationDelay: `${i * 0.04}s`,
        }}
      />
    ))}
  </span>
);

/* ------------------------------------------------------------------ */
/* Pennant bunting — a swaying string of flags along the top edge      */
/* ------------------------------------------------------------------ */
const PENNANT_COLORS = ["#f5c542", "#ff5d8f", "#3fd6f0", "#a779ff", "#d9d9e3"];
const PENNANT_COUNT = 22;

export const Bunting = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-bunting" aria-hidden="true">
      <svg
        className="newyear-bunting__string"
        viewBox="0 0 1000 20"
        preserveAspectRatio="none"
      >
        <path
          d="M0,2 Q125,18 250,2 T500,2 T750,2 T1000,2"
          fill="none"
          stroke="#c9a227"
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {Array.from({ length: PENNANT_COUNT }).map((_, i) => {
        // follow the string's sag: 4 dips across the width
        const t = (i + 0.5) / PENNANT_COUNT;
        const sag = Math.abs(Math.sin(t * Math.PI * 4)) * 9;
        return (
          <span
            key={i}
            className="newyear-pennant"
            style={{
              left: `${t * 100}%`,
              top: `${sag}px`,
              borderTopColor: PENNANT_COLORS[i % PENNANT_COLORS.length],
              animationDelay: `${(i % 6) * 0.25}s`,
            }}
          />
        );
      })}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Twinkling stars — scattered softly behind everything                */
/* ------------------------------------------------------------------ */
const STARS = [
  { left: "3%", top: "62%", size: 8, delay: 0 },
  { left: "9%", top: "40%", size: 6, delay: 1.3 },
  { left: "24%", top: "70%", size: 7, delay: 0.6 },
  { left: "36%", top: "48%", size: 9, delay: 2.1 },
  { left: "41%", top: "78%", size: 6, delay: 1.7 },
  { left: "52%", top: "58%", size: 8, delay: 0.3 },
  { left: "64%", top: "72%", size: 6, delay: 2.6 },
  { left: "68%", top: "38%", size: 7, delay: 1 },
];

export const TwinkleStars = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-stars" aria-hidden="true">
      {STARS.map((s, i) => (
        <span
          key={i}
          className="newyear-star"
          style={{
            left: s.left,
            top: s.top,
            fontSize: `${s.size}px`,
            animationDelay: `${s.delay}s`,
          }}
        >
          ✦
        </span>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Glitter ball — hangs before the header icons, spins and glints      */
/* ------------------------------------------------------------------ */
export const GlitterBall = () => {
  if (!isNewYear()) return null;

  return (
    <div className="newyear-glitterball" aria-hidden="true">
      <div className="newyear-glitterball__string" />
      <div className="newyear-glitterball__ball">
        <span className="newyear-glitterball__glint" />
        <span className="newyear-glitterball__glint newyear-glitterball__glint--2" />
      </div>
      <span className="newyear-glitterball__ray newyear-glitterball__ray--1" />
      <span className="newyear-glitterball__ray newyear-glitterball__ray--2" />
      <span className="newyear-glitterball__ray newyear-glitterball__ray--3" />
    </div>
  );
};

const NewYearTheme = ({ children, className = "" }) => {
  if (!isNewYear()) return children || null;

  return (
    <div className={`newyear-theme-wrapper ${className}`}>
      <TwinkleStars />
      <Fireworks />
      <Bunting />
      <GlitterBall />
      <Confetti />
      <NewYearBanner />
      {children}
    </div>
  );
};

export default NewYearTheme;
